import React, { useState, useRef } from 'react'
import { Scan, CheckCircle, XCircle, Shield, AlertTriangle, Sparkles, Wine, MapPin, Award, Fingerprint, Zap, PenTool, ToggleLeft, ToggleRight, Search, Package } from 'lucide-react'
import api from '../services/api'

const Scanner = () => {
  const [token, setToken] = useState('')
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [scanPhase, setScanPhase] = useState('idle')
  const [particles, setParticles] = useState([])
  const [scanMode, setScanMode] = useState('nfc')
  const [manualForm, setManualForm] = useState({ brand: '', productName: '', batchNumber: '', serialNumber: '' })
  const inputRef = useRef(null)

  const handleScan = async (e) => {
    e.preventDefault()
    if (!token.trim()) return
    setLoading(true)
    setResult(null)
    setScanPhase('scanning')

    await new Promise(r => setTimeout(r, 800))
    setScanPhase('analyzing')
    await new Promise(r => setTimeout(r, 600))
    setScanPhase('verifying')

    try {
      const res = await api.post('/scanner/verify', { tokenHash: token.trim() })
      setResult(res.data)
      setScanPhase(res.data?.scanResult === 'authentic' ? 'verified' : 'flagged')
      if (res.data?.scanResult === 'authentic') {
        const newParticles = Array.from({ length: 24 }, (_, i) => ({
          id: i,
          x: Math.random() * 100,
          delay: Math.random() * 0.5,
          duration: 1.5 + Math.random() * 1.5,
          size: 6 + Math.random() * 10,
          color: ['#A3E635', '#16a34a', '#facc15', '#f59e0b', '#1E4D40'][Math.floor(Math.random() * 5)],
        }))
        setParticles(newParticles)
        setTimeout(() => setParticles([]), 3000)
      }
    } catch (error) {
      setResult({ success: false, error: error.response?.data?.error || 'Verification failed' })
      setScanPhase('flagged')
    } finally {
      setLoading(false)
    }
  }

  const handleManualScan = async (e) => {
    e.preventDefault()
    const { brand, productName, batchNumber, serialNumber } = manualForm
    if (!brand && !productName && !batchNumber && !serialNumber) return
    setLoading(true)
    setResult(null)
    setScanPhase('scanning')

    await new Promise(r => setTimeout(r, 600))
    setScanPhase('analyzing')
    await new Promise(r => setTimeout(r, 500))
    setScanPhase('verifying')

    try {
      const res = await api.post('/scanner/manual', manualForm)
      setResult(res.data)
      setScanPhase(res.data?.scanResult === 'authentic' ? 'verified' : 'flagged')
      if (res.data?.scanResult === 'authentic') {
        const newParticles = Array.from({ length: 24 }, (_, i) => ({
          id: i, x: Math.random() * 100, delay: Math.random() * 0.5,
          duration: 1.5 + Math.random() * 1.5, size: 6 + Math.random() * 10,
          color: ['#A3E635', '#16a34a', '#facc15', '#f59e0b', '#1E4D40'][Math.floor(Math.random() * 5)],
        }))
        setParticles(newParticles)
        setTimeout(() => setParticles([]), 3000)
      }
    } catch (error) {
      setResult({ success: false, error: error.response?.data?.error || 'Verification failed' })
      setScanPhase('flagged')
    } finally {
      setLoading(false)
    }
  }

  const resetScanner = () => {
    setToken('')
    setResult(null)
    setScanPhase('idle')
    setParticles([])
    setManualForm({ brand: '', productName: '', batchNumber: '', serialNumber: '' })
    inputRef.current?.focus()
  }

  const scanMessages = {
    scanning: 'Reading NFC signature...',
    analyzing: 'Analyzing authentication data...',
    verifying: 'Cross-referencing with VerifiAI database...',
  }

  return (
    <div className="max-w-lg mx-auto space-y-6 relative">
      {particles.length > 0 && (
        <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
          {particles.map(p => (
            <div key={p.id} className="absolute" style={{
              left: `${p.x}%`,
              top: '-20px',
              width: `${p.size}px`,
              height: `${p.size}px`,
              backgroundColor: p.color,
              borderRadius: p.size > 12 ? '50%' : '2px',
              animation: `confettiFall ${p.duration}s ease-in ${p.delay}s forwards`,
              transform: `rotate(${Math.random() * 360}deg)`,
            }} />
          ))}
        </div>
      )}

      <style>{`
        @keyframes confettiFall {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        @keyframes pulseRing {
          0% { transform: scale(0.8); opacity: 0.8; }
          50% { transform: scale(1.2); opacity: 0.3; }
          100% { transform: scale(0.8); opacity: 0.8; }
        }
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>

      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900">Verify Your Bottle</h1>
        <p className="text-gray-500 mt-1">Tap, scan, or enter details manually</p>
      </div>

      {!result && !loading && (
        <div className="flex items-center justify-center">
          <div className="inline-flex bg-white rounded-2xl p-1 shadow-sm border border-gray-100">
            <button
              onClick={() => setScanMode('nfc')}
              className={`flex items-center space-x-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                scanMode === 'nfc' ? 'bg-[#1E4D40] text-white shadow-md' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Fingerprint className="h-4 w-4" />
              <span>NFC Scan</span>
            </button>
            <button
              onClick={() => setScanMode('manual')}
              className={`flex items-center space-x-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                scanMode === 'manual' ? 'bg-[#1E4D40] text-white shadow-md' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <PenTool className="h-4 w-4" />
              <span>Manual Entry</span>
            </button>
          </div>
        </div>
      )}

      <div className={`relative rounded-3xl overflow-hidden transition-all duration-500 ${
        scanPhase === 'verified' ? 'bg-gradient-to-br from-green-50 via-emerald-50 to-lime-50 ring-2 ring-green-300' :
        scanPhase === 'flagged' ? 'bg-gradient-to-br from-red-50 via-orange-50 to-amber-50 ring-2 ring-red-300' :
        loading ? 'bg-gradient-to-br from-amber-50 via-yellow-50 to-lime-50' :
        'bg-white'
      } shadow-sm p-8`}>

        <div className="flex justify-center mb-6">
          <div className="relative">
            {loading && (
              <>
                <div className="absolute inset-0 -m-4 rounded-full border-2 border-[#A3E635]/40" style={{ animation: 'pulseRing 1.5s ease-in-out infinite' }} />
                <div className="absolute inset-0 -m-8 rounded-full border-2 border-[#1E4D40]/20" style={{ animation: 'pulseRing 1.5s ease-in-out infinite 0.3s' }} />
                <div className="absolute inset-0 -m-12 rounded-full border border-[#A3E635]/10" style={{ animation: 'pulseRing 1.5s ease-in-out infinite 0.6s' }} />
              </>
            )}
            <div className={`w-28 h-28 rounded-3xl flex items-center justify-center transition-all duration-500 ${
              scanPhase === 'verified' ? 'bg-gradient-to-br from-green-400 to-emerald-500 shadow-lg shadow-green-200 scale-110' :
              scanPhase === 'flagged' ? 'bg-gradient-to-br from-red-400 to-red-500 shadow-lg shadow-red-200 scale-110' :
              loading ? 'bg-gradient-to-br from-amber-400 to-yellow-500 shadow-lg shadow-amber-200' :
              'bg-gradient-to-br from-[#1E4D40] to-[#2D6A5A] shadow-lg shadow-[#1E4D40]/20'
            }`}>
              {scanPhase === 'verified' ? (
                <CheckCircle className="h-14 w-14 text-white drop-shadow-md" />
              ) : scanPhase === 'flagged' ? (
                <XCircle className="h-14 w-14 text-white drop-shadow-md" />
              ) : loading ? (
                <Scan className="h-14 w-14 text-white drop-shadow-md animate-pulse" />
              ) : (
                <Wine className="h-14 w-14 text-white drop-shadow-md" />
              )}
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-center mb-6 space-y-3">
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden max-w-xs mx-auto">
              <div className="h-full rounded-full" style={{
                background: 'linear-gradient(90deg, #1E4D40, #A3E635, #1E4D40)',
                backgroundSize: '200% 100%',
                animation: 'shimmer 1.5s linear infinite',
                width: scanPhase === 'scanning' ? '33%' : scanPhase === 'analyzing' ? '66%' : '90%',
                transition: 'width 0.5s ease-out',
              }} />
            </div>
            <p className="text-sm font-medium text-gray-600 animate-pulse">
              {scanMessages[scanPhase] || 'Processing...'}
            </p>
          </div>
        )}

        {!result && !loading && scanMode === 'nfc' && (
          <form onSubmit={handleScan} className="space-y-4">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#1E4D40]/10 text-[#1E4D40] text-xs font-medium mb-2 mx-auto block text-center">
              <Fingerprint className="h-3 w-3 mr-1 inline" /> NFC Authentication
            </div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Scan className="h-5 w-5 text-gray-400" />
              </div>
              <input
                ref={inputRef}
                type="text"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Tap NFC or enter token ID..."
                className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#1E4D40]/20 focus:border-[#1E4D40] text-center text-lg font-medium transition-all duration-200 bg-gray-50/50"
              />
            </div>
            <button
              type="submit"
              disabled={!token.trim()}
              className="w-full py-4 px-6 bg-gradient-to-r from-[#1E4D40] to-[#2D6A5A] text-white rounded-2xl font-semibold text-lg hover:from-[#163a30] hover:to-[#1E4D40] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg shadow-[#1E4D40]/20 hover:shadow-xl hover:shadow-[#1E4D40]/30 active:scale-[0.98]"
            >
              <Shield className="h-5 w-5" />
              <span>Verify Authenticity</span>
            </button>
            <p className="text-center text-xs text-gray-400">
              Try: <button type="button" onClick={() => setToken('NFC-ABSOLUT-001-0001')} className="text-[#1E4D40] hover:underline font-medium">NFC-ABSOLUT-001-0001</button>
            </p>
          </form>
        )}

        {!result && !loading && scanMode === 'manual' && (
          <form onSubmit={handleManualScan} className="space-y-4">
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-medium mb-2 mx-auto block text-center">
              <PenTool className="h-3 w-3 mr-1 inline" /> Manual Product Lookup
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="relative">
                <label className="block text-xs font-medium text-gray-500 mb-1">Brand</label>
                <input
                  type="text"
                  value={manualForm.brand}
                  onChange={(e) => setManualForm({...manualForm, brand: e.target.value})}
                  placeholder="e.g. Absolut"
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E4D40]/20 focus:border-[#1E4D40] text-sm transition-all bg-gray-50/50"
                />
              </div>
              <div className="relative">
                <label className="block text-xs font-medium text-gray-500 mb-1">Product Name</label>
                <input
                  type="text"
                  value={manualForm.productName}
                  onChange={(e) => setManualForm({...manualForm, productName: e.target.value})}
                  placeholder="e.g. Absolut Vodka"
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E4D40]/20 focus:border-[#1E4D40] text-sm transition-all bg-gray-50/50"
                />
              </div>
              <div className="relative">
                <label className="block text-xs font-medium text-gray-500 mb-1">Batch Number</label>
                <input
                  type="text"
                  value={manualForm.batchNumber}
                  onChange={(e) => setManualForm({...manualForm, batchNumber: e.target.value})}
                  placeholder="e.g. BATCH-2024-001"
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E4D40]/20 focus:border-[#1E4D40] text-sm transition-all bg-gray-50/50"
                />
              </div>
              <div className="relative">
                <label className="block text-xs font-medium text-gray-500 mb-1">Serial Number</label>
                <input
                  type="text"
                  value={manualForm.serialNumber}
                  onChange={(e) => setManualForm({...manualForm, serialNumber: e.target.value})}
                  placeholder="e.g. SN-001"
                  className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E4D40]/20 focus:border-[#1E4D40] text-sm transition-all bg-gray-50/50"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={!manualForm.brand && !manualForm.productName && !manualForm.batchNumber && !manualForm.serialNumber}
              className="w-full py-4 px-6 bg-gradient-to-r from-[#1E4D40] to-[#2D6A5A] text-white rounded-2xl font-semibold text-lg hover:from-[#163a30] hover:to-[#1E4D40] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg shadow-[#1E4D40]/20 hover:shadow-xl hover:shadow-[#1E4D40]/30 active:scale-[0.98]"
            >
              <Search className="h-5 w-5" />
              <span>Look Up Product</span>
            </button>
            <p className="text-center text-xs text-gray-400">
              Enter at least one field to search. Try brand: <button type="button" onClick={() => setManualForm({...manualForm, brand: 'Absolut'})} className="text-[#1E4D40] hover:underline font-medium">Absolut</button>
            </p>
          </form>
        )}

        {result && scanPhase === 'verified' && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-green-100 text-green-700 text-sm font-semibold mb-3">
                <Award className="h-4 w-4 mr-1.5" /> Verified Authentic
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                {result.product?.brand === 'Jameson' ? "Slainte! It's the real deal" :
                 result.product?.brand === 'Absolut' ? "Absolut-ly genuine!" :
                 result.product?.brand === 'Heineken' ? "Green light - You're good!" :
                 result.product?.brand?.includes('KWV') ? "Perfection verified!" :
                 "Cheers! This one's legit"}
              </h2>
              <p className="text-gray-500 mt-1">Your bottle has been authenticated by VerifiAI</p>
            </div>

            {result.product && (
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-5 border border-green-100 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wider">Product</p>
                    <p className="text-lg font-bold text-gray-900">{result.product.name}</p>
                  </div>
                  {result.confidence && (
                    <div className="text-right">
                      <p className="text-xs text-gray-400 uppercase tracking-wider">Confidence</p>
                      <p className="text-lg font-bold text-green-600">{(result.confidence * 100).toFixed(0)}%</p>
                    </div>
                  )}
                </div>

                <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />

                <div className="grid grid-cols-2 gap-4">
                  {result.product.brand && (
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 rounded-lg bg-[#1E4D40]/10 flex items-center justify-center">
                        <Wine className="h-4 w-4 text-[#1E4D40]" />
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase">Brand</p>
                        <p className="text-sm font-semibold text-gray-800">{result.product.brand}</p>
                      </div>
                    </div>
                  )}
                  {result.product.manufacturer && (
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 rounded-lg bg-[#1E4D40]/10 flex items-center justify-center">
                        <Shield className="h-4 w-4 text-[#1E4D40]" />
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase">Maker</p>
                        <p className="text-sm font-semibold text-gray-800">{result.product.manufacturer}</p>
                      </div>
                    </div>
                  )}
                  {result.product.category && (
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 rounded-lg bg-[#1E4D40]/10 flex items-center justify-center">
                        <Sparkles className="h-4 w-4 text-[#1E4D40]" />
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase">Category</p>
                        <p className="text-sm font-semibold text-gray-800">{result.product.category}</p>
                      </div>
                    </div>
                  )}
                  {result.product.batchNumber && (
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 rounded-lg bg-[#1E4D40]/10 flex items-center justify-center">
                        <Fingerprint className="h-4 w-4 text-[#1E4D40]" />
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase">Batch</p>
                        <p className="text-sm font-semibold text-gray-800">{result.product.batchNumber}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />

                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-400">Scanned {new Date().toLocaleString()}</span>
                  <span className="text-green-600 font-medium flex items-center"><Shield className="h-3 w-3 mr-1" /> VerifiAI Protected</span>
                </div>
              </div>
            )}

            <button onClick={resetScanner} className="w-full py-3 px-4 bg-[#1E4D40] text-white rounded-2xl font-medium hover:bg-[#163a30] transition-all flex items-center justify-center space-x-2">
              <Scan className="h-4 w-4" />
              <span>Scan Another Bottle</span>
            </button>
          </div>
        )}

        {result && scanPhase === 'flagged' && (
          <div className="space-y-6">
            <div className="text-center">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-red-100 text-red-700 text-sm font-semibold mb-3">
                <AlertTriangle className="h-4 w-4 mr-1.5" /> Warning
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Hold up - Something's off</h2>
              <p className="text-gray-500 mt-1">{result.message || result.error || 'This product could not be verified'}</p>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-5 border border-red-100">
              <div className="flex items-start space-x-3">
                <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Shield className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-red-800">What should you do?</p>
                  <ul className="mt-2 space-y-1.5 text-sm text-gray-600">
                    <li className="flex items-start space-x-2"><span className="text-red-400 mt-0.5">1.</span><span>Do not consume this product</span></li>
                    <li className="flex items-start space-x-2"><span className="text-red-400 mt-0.5">2.</span><span>Report it to the retailer where you purchased it</span></li>
                    <li className="flex items-start space-x-2"><span className="text-red-400 mt-0.5">3.</span><span>File a report through our Reports section</span></li>
                  </ul>
                </div>
              </div>
            </div>

            <button onClick={resetScanner} className="w-full py-3 px-4 bg-gray-800 text-white rounded-2xl font-medium hover:bg-gray-900 transition-all flex items-center justify-center space-x-2">
              <Scan className="h-4 w-4" />
              <span>Try Another Scan</span>
            </button>
          </div>
        )}
      </div>

      {!result && !loading && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { icon: Zap, label: 'Instant', desc: 'Results in < 2 sec' },
            { icon: Shield, label: 'AI-Powered', desc: '87%+ accuracy' },
            { icon: MapPin, label: 'Tracked', desc: 'Location logged' },
          ].map((f, i) => (
            <div key={i} className="bg-white rounded-2xl p-3 shadow-sm text-center">
              <div className="w-8 h-8 rounded-xl bg-[#1E4D40]/10 flex items-center justify-center mx-auto mb-2">
                <f.icon className="h-4 w-4 text-[#1E4D40]" />
              </div>
              <p className="text-xs font-semibold text-gray-800">{f.label}</p>
              <p className="text-[10px] text-gray-400">{f.desc}</p>
            </div>
          ))}
        </div>
      )}

      {!result && !loading && (
        <div className="bg-white rounded-2xl p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-gray-800 mb-3">How it works</h3>
          <div className="flex items-start space-x-4">
            {[
              { step: '1', title: 'Tap or Enter', desc: 'Hold your phone to the NFC tag or type the token ID' },
              { step: '2', title: 'AI Verifies', desc: 'Our AI cross-references the token against our database' },
              { step: '3', title: 'Get Results', desc: 'Instant confirmation with full product details' },
            ].map((s, i) => (
              <div key={i} className="flex-1 text-center">
                <div className="w-7 h-7 rounded-full bg-[#1E4D40] text-white text-xs font-bold flex items-center justify-center mx-auto mb-1.5">{s.step}</div>
                <p className="text-xs font-semibold text-gray-800">{s.title}</p>
                <p className="text-[10px] text-gray-400 mt-0.5 leading-tight">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default Scanner
